import {
  parseUserMentionLink,
  serializeUserMentionAttrs,
  type UserMentionAttrs,
  userMentionTokenizer,
} from '@bangle.io/markdown-syntax';
import type { PluginSimple } from 'markdown-it';
import { type CollectionType, collection } from './common';
import type { Command, DOMOutputSpec, NodeSpec, PMNode } from './pm';

export type { UserMentionAttrs } from '@bangle.io/markdown-syntax';

export type UserMentionConfig = {
  name?: string;
};

type RequiredConfig = Required<UserMentionConfig>;

const DEFAULT_CONFIG: RequiredConfig = {
  name: 'user_mention',
};

/**
 * Inline atom for cloud collaborator mentions. Markdown form:
 * `[@Display Name](mention:userId)`.
 */
export function setupUserMention(userConfig?: UserMentionConfig) {
  const config: RequiredConfig = {
    ...DEFAULT_CONFIG,
    ...userConfig,
  };
  const { name } = config;

  const nodes = {
    [name]: {
      inline: true,
      group: 'inline',
      atom: true,
      selectable: true,
      attrs: {
        userId: { default: '' },
        label: { default: '' },
      },
      parseDOM: [
        {
          tag: 'span[data-user-mention]',
          getAttrs: (dom: HTMLElement) => {
            const userId = dom.getAttribute('data-user-mention');
            const label =
              dom.getAttribute('data-mention-label') ?? dom.textContent;
            if (!userId || !label) {
              return false;
            }
            return { userId, label };
          },
        },
      ],
      toDOM: (node: PMNode): DOMOutputSpec => {
        const attrs = node.attrs as UserMentionAttrs;
        return [
          'span',
          {
            'data-user-mention': attrs.userId,
            'data-mention-label': attrs.label,
            class: 'sn-user-mention',
            spellcheck: 'false',
          },
          `@${attrs.label}`,
        ];
      },
      leafText: (node: PMNode) => `@${(node.attrs as UserMentionAttrs).label}`,
    } satisfies NodeSpec,
  };

  const tokenizerPlugins: PluginSimple[] = [userMentionTokenizer];

  return collection({
    id: `user-mention-${name}`,
    nodes,
    command: {
      insertUserMention: insertUserMention(config),
    },
    markdown: markdown(config, tokenizerPlugins),
  });
}

function insertUserMention(
  config: RequiredConfig,
): (attrs: UserMentionAttrs) => Command {
  return (attrs) => (state, dispatch) => {
    const type = state.schema.nodes[config.name];
    if (!type || !attrs.userId || !attrs.label) {
      return false;
    }
    const node = type.create(attrs);
    if (dispatch) {
      dispatch(state.tr.replaceSelectionWith(node).scrollIntoView());
    }
    return true;
  };
}

function markdown(
  config: RequiredConfig,
  tokenizerPlugins: PluginSimple[],
): CollectionType['markdown'] {
  return {
    tokenizerPlugins,
    nodes: {
      [config.name]: {
        toMarkdown(state, node) {
          state.write(
            serializeUserMentionAttrs(node.attrs as UserMentionAttrs),
          );
        },
        parseMarkdown: {
          user_mention: {
            node: config.name,
            getAttrs: (tok) => {
              const meta = tok.meta as UserMentionAttrs | undefined;
              if (meta?.userId && meta.label) {
                return meta;
              }
              return parseUserMentionLink(
                tok.content,
                tok.attrGet?.('href') ?? '',
              );
            },
          },
        },
      },
    },
  };
}
